import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  PartsWithInvalidUom,
  PartsWithMultipleManufacturers,
  PartsWithoutManufacturer,
  UnknownManufacturers,
} from 'bom/components';
import { ProblemNumber } from 'bom/elements';
import { ImportStep, ManufacturerValidationStatus, SolveProblemsStep } from 'bom/enums';
import {
  useGetBomImportNotValidParts,
  useGetBomImportValidateManufacturers,
  useGetBomImportValidateParts,
  useGetBomImportValidateUoms,
} from 'bom/hooks';
import { useBomPartsImportContext } from 'bom/states';
import {
  IImportNotValidPartsFilters,
  IImportPart,
  IValidateManufacturers,
  IValidateUoms,
} from 'bom/types';
import { InnerSteps } from 'core/components';
import { InnerStepsProvider, useStepsContext } from 'core/states';
import { GetManyResponse } from 'core/types';

import { SubtitleWrapper, Wrapper } from './style';
import { getStepSubtitle, isStepCompleted } from './utils';

export const SolveProblems = () => {
  const [manufacturersValidated, setManufacturersValidated] = useState(false);
  const [partsValidated, setPartsValidated] = useState(false);
  const [uomsValidated, setUomsValidated] = useState(false);

  const { t } = useTranslation();
  const { partImportId, problems, setProblems } = useBomPartsImportContext();
  const { activeStep } = useStepsContext();

  const { record: validatedManufacturers, isLoading: isValidateManufacturersLoading } =
    useGetBomImportValidateManufacturers(
      {
        partImport: partImportId,
      },
      activeStep?.type === ImportStep.SOLVE_PROBLEMS &&
        !problems[SolveProblemsStep.UNKNOWN_MANUFACTURERS] &&
        !manufacturersValidated,
      (data: IValidateManufacturers) => {
        const unknownManufacturers = data?.unknown || {};

        setProblems((prev) => ({
          ...prev,
          [SolveProblemsStep.UNKNOWN_MANUFACTURERS]: {
            solved: 0,
            unsolved: Object.keys(unknownManufacturers).length,
          },
        }));

        setManufacturersValidated(true);
      }
    );

  const { record: validatedParts, isLoading: isValidatePartsLoading } =
    useGetBomImportValidateParts(
      {
        partImport: partImportId,
      },
      manufacturersValidated && !partsValidated,
      () => {
        setPartsValidated(true);
      }
    );

  // Contract: validate UOMs only AFTER manufacturers, so the MISMATCH part lookup resolves.
  const { record: validatedUoms, isLoading: isValidateUomsLoading } = useGetBomImportValidateUoms(
    { partImport: partImportId },
    manufacturersValidated && !uomsValidated,
    (data: IValidateUoms) => {
      setProblems((prev) => ({
        ...prev,
        [SolveProblemsStep.MEASURE_CONFLICT]: {
          solved: 0,
          unsolved: (data?.unknown ?? 0) + (data?.mismatch ?? 0),
        },
      }));

      setUomsValidated(true);
    }
  );

  const filters: IImportNotValidPartsFilters = useMemo(
    () => ({
      importId: partImportId,
      manufacturerValidationStatus: null,
      omit: '"0"',
    }),
    [partImportId]
  );

  useGetBomImportNotValidParts(
    {
      filters: {
        ...filters,
        manufacturerValidationStatus: ManufacturerValidationStatus.UNKNOWN,
      },
    },
    partsValidated,
    (data: GetManyResponse<IImportPart>) => {
      if (!partsValidated) {
        return;
      }

      const records = data.records || [];

      setProblems((prev) => ({
        ...prev,
        [SolveProblemsStep.ASSIGN_MANUFACTURERS]: {
          solved: 0,
          unsolved: records.length,
        },
      }));
    }
  );

  useGetBomImportNotValidParts(
    {
      filters: {
        ...filters,
        manufacturerValidationStatus: ManufacturerValidationStatus.MULTIPLE_MANUFACTURERS,
      },
    },
    partsValidated,
    (data: GetManyResponse<IImportPart>) => {
      if (!partsValidated) {
        return;
      }

      const records = data.records || [];

      setProblems((prev) => ({
        ...prev,
        [SolveProblemsStep.MULTIPLE_MANUFACTURERS]: {
          solved: 0,
          unsolved: records.length,
        },
      }));
    }
  );

  const clearProblems = useCallback(() => {
    setProblems({
      [SolveProblemsStep.UNKNOWN_MANUFACTURERS]: null,
      [SolveProblemsStep.ASSIGN_MANUFACTURERS]: null,
      [SolveProblemsStep.MULTIPLE_MANUFACTURERS]: null,
      [SolveProblemsStep.MEASURE_CONFLICT]: null,
    });

    setManufacturersValidated(false);
    setPartsValidated(false);
    setUomsValidated(false);
  }, [setProblems]);

  // Reset on import change (reopened drawer / new import) or unmount, so the step never inherits
  // the previous run's solved steps. Clears problems AND the local validation flags together —
  // clearing only problems leaves the flags stale and the steps skip ahead.
  useEffect(() => {
    return () => clearProblems();
  }, [partImportId, clearProblems]);

  const totalProblems = useMemo(
    () =>
      Object.values(problems).reduce(
        (totals, problem) => {
          totals.solved += problem?.solved;
          totals.unsolved += problem?.unsolved;

          return totals;
        },
        { solved: 0, unsolved: 0 }
      ),
    [problems]
  );

  const steps = useMemo(() => {
    return [
      {
        title: t('titleUnknownManufacturers'),
        subtitle: getStepSubtitle(problems, SolveProblemsStep.UNKNOWN_MANUFACTURERS),
        type: SolveProblemsStep.UNKNOWN_MANUFACTURERS,
        content: (
          <UnknownManufacturers
            records={validatedManufacturers?.unknown}
            isLoading={isValidateManufacturersLoading}
            isValid={validatedManufacturers?.valid}
          />
        ),
        completed: isStepCompleted(problems, SolveProblemsStep.UNKNOWN_MANUFACTURERS),
        disabled: false,
      },
      {
        title: t('titleAssignManufacturers'),
        subtitle: getStepSubtitle(problems, SolveProblemsStep.ASSIGN_MANUFACTURERS),
        type: SolveProblemsStep.ASSIGN_MANUFACTURERS,
        content: <PartsWithoutManufacturer isValid={validatedParts?.valid} />,
        completed: isStepCompleted(problems, SolveProblemsStep.ASSIGN_MANUFACTURERS),
        disabled: false,
      },
      {
        title: t('titleMultipleManufacturers'),
        subtitle: getStepSubtitle(problems, SolveProblemsStep.MULTIPLE_MANUFACTURERS),
        type: SolveProblemsStep.MULTIPLE_MANUFACTURERS,
        content: <PartsWithMultipleManufacturers isValid={validatedParts?.valid} />,
        completed: isStepCompleted(problems, SolveProblemsStep.MULTIPLE_MANUFACTURERS),
        disabled: false,
      },
      {
        title: t('titleMeasureConflict'),
        subtitle: getStepSubtitle(problems, SolveProblemsStep.MEASURE_CONFLICT),
        type: SolveProblemsStep.MEASURE_CONFLICT,
        content: <PartsWithInvalidUom isValid={validatedUoms?.valid} />,
        completed: isStepCompleted(problems, SolveProblemsStep.MEASURE_CONFLICT),
        disabled: false,
      },
    ];
  }, [
    t,
    problems,
    validatedManufacturers,
    isValidateManufacturersLoading,
    validatedParts,
    validatedUoms,
  ]);

  return (
    <Wrapper>
      <SubtitleWrapper>
        <ProblemNumber number={totalProblems.unsolved} total />
        <ProblemNumber number={totalProblems.solved} solved total />
      </SubtitleWrapper>

      <InnerStepsProvider
        key={JSON.stringify(
          isValidateManufacturersLoading || isValidatePartsLoading || isValidateUomsLoading
        )}
        steps={steps}
      >
        <InnerSteps />
      </InnerStepsProvider>
    </Wrapper>
  );
};
